import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import { useSubmitFeedback } from '../api/use-docs.mutation';
import { DOCS_FEEDBACK_REASON_MAX } from '../model/docs.type';

/** 이 브라우저가 이 문서에 이미 답했는지. 서버 왕복 없이 상태를 복원한다. */
const storageKey = (docId: string) => `docs-feedback:${docId}`;

/**
 * localStorage 는 시크릿 창·저장 차단 설정에서 접근 자체가 throw 한다.
 * 피드백은 부가 기능이라 여기서 실패해도 화면이 죽으면 안 된다.
 */
function readVoted(docId: string): boolean {
  try {
    return localStorage.getItem(storageKey(docId)) !== null;
  } catch {
    return false;
  }
}

function markVoted(docId: string, helpful: boolean): void {
  try {
    localStorage.setItem(storageKey(docId), helpful ? 'y' : 'n');
  } catch {
    /* 저장 못 해도 서버에는 이미 들어갔다 — 새로고침 시 다시 물어볼 뿐이다. */
  }
}

const BTN =
  'rounded-lg border border-neutral-200 px-3.5 py-1.5 text-[13px] font-semibold transition-colors hover:border-brand-300 disabled:opacity-40 dark:border-white/12';

interface DocsFeedbackProps {
  docId: string;
  title: string;
}

/**
 * 문서 하단 "이 문서가 도움이 되었나요?".
 *
 * **집계 숫자를 독자에게 보여주지 않는다.** 도움말에 "👍 3 👎 11" 이 붙어 있으면
 * 읽기도 전에 문서를 불신하게 되고, 독자가 그 숫자로 할 수 있는 일도 없다.
 * 숫자는 어느 문서를 고칠지 정하는 내부 신호라 `/docs/manage` 에만 둔다.
 *
 * '아니오' 뒤의 한 줄이 이 기능의 실제 가치다 — 숫자는 어느 문서인지만 알려주고,
 * 무엇을 고쳐야 하는지는 그 문장이 알려준다.
 *
 * ⚠️ 호출부에서 `key={docId}` 를 반드시 준다. 문서를 옮겨다닐 때 이 컴포넌트가
 *    재사용되면 이전 문서의 '의견 감사합니다' 가 그대로 남는다. effect 로
 *    되돌리는 것보다 key 로 새로 마운트하는 쪽이 맞다.
 */
export function DocsFeedback({ docId, title }: DocsFeedbackProps) {
  const submit = useSubmitFeedback();
  // 초기값 계산은 첫 렌더 1회뿐 — localStorage 를 렌더마다 읽지 않는다.
  const [phase, setPhase] = useState<'ask' | 'reason' | 'done'>(() =>
    readVoted(docId) ? 'done' : 'ask',
  );
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState(false);

  const inquiryHref = `${PATHS.inquiryWrite()}?subject=${encodeURIComponent(`[도움말] ${title}`)}`;

  const send = (helpful: boolean, text?: string) => {
    setFailed(false);
    submit.mutate(
      { docId, helpful, reason: text ?? null },
      {
        onSuccess: () => {
          markVoted(docId, helpful);
          setPhase('done');
        },
        // 실패해도 '고맙습니다'를 띄우지 않는다 — 전달되지 않은 의견을 전달됐다고
        // 말하면, 사용자는 답을 기다리고 대표님은 영영 못 본다.
        onError: () => setFailed(true),
      },
    );
  };

  return (
    <section className="mt-7 border-t border-neutral-200 pt-6 text-[13.5px] text-neutral-500 dark:border-white/12 dark:text-neutral-400">
      {phase === 'ask' && (
        <div className="flex flex-wrap items-center gap-2.5">
          <span>이 문서가 도움이 되었나요?</span>
          <button
            type="button"
            className={BTN}
            disabled={submit.isPending}
            onClick={() => send(true)}
          >
            예
          </button>
          <button
            type="button"
            className={BTN}
            disabled={submit.isPending}
            onClick={() => setPhase('reason')}
          >
            아니오
          </button>
        </div>
      )}

      {phase === 'reason' && (
        <div className="space-y-2.5">
          <label className="block" htmlFor="docs-feedback-reason">
            무엇이 부족했나요?
            <span className="ml-1.5 text-neutral-400">
              — 한 줄이면 충분합니다. 답변이 필요하면 1:1 문의를 이용해 주세요
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <input
              id="docs-feedback-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={DOCS_FEEDBACK_REASON_MAX}
              placeholder="예: 화면이 설명과 달라요"
              className="min-w-64 flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-[13.5px] outline-none focus:border-brand-400 dark:border-white/15 dark:bg-white/5"
              onKeyDown={(e) => {
                if (e.key === 'Enter') send(false, reason.trim() || undefined);
              }}
            />
            <button
              type="button"
              className={BTN}
              disabled={submit.isPending}
              onClick={() => send(false, reason.trim() || undefined)}
            >
              {submit.isPending ? '보내는 중…' : '보내기'}
            </button>
            <button
              type="button"
              className={BTN}
              disabled={submit.isPending}
              onClick={() => send(false)}
            >
              건너뛰기
            </button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div className="flex flex-wrap items-center gap-2.5">
          <span>의견 감사합니다. 문서를 개선할 때 사용하겠습니다.😄</span>
          <Link
            to={inquiryHref}
            className="rounded-lg border border-neutral-200 px-3.5 py-1.5 text-[13px] font-semibold text-brand-600 transition-colors hover:border-brand-300 dark:border-white/12 dark:text-brand-400"
          >
            1:1 문의하기 →
          </Link>
        </div>
      )}

      {failed && (
        <p className="mt-2.5 text-danger">
          의견을 보내지 못했습니다. 잠시 뒤 다시 시도해 주세요.
        </p>
      )}
    </section>
  );
}
